import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Receipt } from "lucide-react";

// ISO 3166-1 countries with common billing metadata
const COUNTRIES = [
  { code: "AF", name: "Afghanistan" },
  { code: "AL", name: "Albania" },
  { code: "DZ", name: "Algeria" },
  { code: "AD", name: "Andorra" },
  { code: "AO", name: "Angola" },
  { code: "AG", name: "Antigua and Barbuda" },
  { code: "AR", name: "Argentina" },
  { code: "AM", name: "Armenia" },
  { code: "AU", name: "Australia" },
  { code: "AT", name: "Austria" },
  { code: "AZ", name: "Azerbaijan" },
  { code: "BS", name: "Bahamas" },
  { code: "BH", name: "Bahrain" },
  { code: "BD", name: "Bangladesh" },
  { code: "BB", name: "Barbados" },
  { code: "BY", name: "Belarus" },
  { code: "BE", name: "Belgium" },
  { code: "BZ", name: "Belize" },
  { code: "BJ", name: "Benin" },
  { code: "BT", name: "Bhutan" },
  { code: "BO", name: "Bolivia" },
  { code: "BA", name: "Bosnia and Herzegovina" },
  { code: "BW", name: "Botswana" },
  { code: "BR", name: "Brazil" },
  { code: "BN", name: "Brunei" },
  { code: "BG", name: "Bulgaria" },
  { code: "BF", name: "Burkina Faso" },
  { code: "BI", name: "Burundi" },
  { code: "KH", name: "Cambodia" },
  { code: "CM", name: "Cameroon" },
  { code: "CA", name: "Canada" },
  { code: "CV", name: "Cape Verde" },
  { code: "CF", name: "Central African Republic" },
  { code: "TD", name: "Chad" },
  { code: "CL", name: "Chile" },
  { code: "CN", name: "China" },
  { code: "CO", name: "Colombia" },
  { code: "KM", name: "Comoros" },
  { code: "CG", name: "Congo" },
  { code: "CR", name: "Costa Rica" },
  { code: "HR", name: "Croatia" },
  { code: "CU", name: "Cuba" },
  { code: "CY", name: "Cyprus" },
  { code: "CZ", name: "Czech Republic" },
  { code: "DK", name: "Denmark" },
  { code: "DJ", name: "Djibouti" },
  { code: "DM", name: "Dominica" },
  { code: "DO", name: "Dominican Republic" },
  { code: "EC", name: "Ecuador" },
  { code: "EG", name: "Egypt" },
  { code: "SV", name: "El Salvador" },
  { code: "GQ", name: "Equatorial Guinea" },
  { code: "ER", name: "Eritrea" },
  { code: "EE", name: "Estonia" },
  { code: "ET", name: "Ethiopia" },
  { code: "FJ", name: "Fiji" },
  { code: "FI", name: "Finland" },
  { code: "FR", name: "France" },
  { code: "GA", name: "Gabon" },
  { code: "GM", name: "Gambia" },
  { code: "GE", name: "Georgia" },
  { code: "DE", name: "Germany" },
  { code: "GH", name: "Ghana" },
  { code: "GR", name: "Greece" },
  { code: "GD", name: "Grenada" },
  { code: "GT", name: "Guatemala" },
  { code: "GN", name: "Guinea" },
  { code: "GW", name: "Guinea-Bissau" },
  { code: "GY", name: "Guyana" },
  { code: "HT", name: "Haiti" },
  { code: "HN", name: "Honduras" },
  { code: "HK", name: "Hong Kong" },
  { code: "HU", name: "Hungary" },
  { code: "IS", name: "Iceland" },
  { code: "IN", name: "India" },
  { code: "ID", name: "Indonesia" },
  { code: "IR", name: "Iran" },
  { code: "IQ", name: "Iraq" },
  { code: "IE", name: "Ireland" },
  { code: "IL", name: "Israel" },
  { code: "IT", name: "Italy" },
  { code: "JM", name: "Jamaica" },
  { code: "JP", name: "Japan" },
  { code: "JO", name: "Jordan" },
  { code: "KZ", name: "Kazakhstan" },
  { code: "KE", name: "Kenya" },
  { code: "KI", name: "Kiribati" },
  { code: "KW", name: "Kuwait" },
  { code: "KG", name: "Kyrgyzstan" },
  { code: "LA", name: "Laos" },
  { code: "LV", name: "Latvia" },
  { code: "LB", name: "Lebanon" },
  { code: "LS", name: "Lesotho" },
  { code: "LR", name: "Liberia" },
  { code: "LY", name: "Libya" },
  { code: "LI", name: "Liechtenstein" },
  { code: "LT", name: "Lithuania" },
  { code: "LU", name: "Luxembourg" },
  { code: "MO", name: "Macau" },
  { code: "MG", name: "Madagascar" },
  { code: "MW", name: "Malawi" },
  { code: "MY", name: "Malaysia" },
  { code: "MV", name: "Maldives" },
  { code: "ML", name: "Mali" },
  { code: "MT", name: "Malta" },
  { code: "MH", name: "Marshall Islands" },
  { code: "MR", name: "Mauritania" },
  { code: "MU", name: "Mauritius" },
  { code: "MX", name: "Mexico" },
  { code: "FM", name: "Micronesia" },
  { code: "MD", name: "Moldova" },
  { code: "MC", name: "Monaco" },
  { code: "MN", name: "Mongolia" },
  { code: "ME", name: "Montenegro" },
  { code: "MA", name: "Morocco" },
  { code: "MZ", name: "Mozambique" },
  { code: "MM", name: "Myanmar" },
  { code: "NA", name: "Namibia" },
  { code: "NR", name: "Nauru" },
  { code: "NP", name: "Nepal" },
  { code: "NL", name: "Netherlands" },
  { code: "NZ", name: "New Zealand" },
  { code: "NI", name: "Nicaragua" },
  { code: "NE", name: "Niger" },
  { code: "NG", name: "Nigeria" },
  { code: "KP", name: "North Korea" },
  { code: "MK", name: "North Macedonia" },
  { code: "NO", name: "Norway" },
  { code: "OM", name: "Oman" },
  { code: "PK", name: "Pakistan" },
  { code: "PW", name: "Palau" },
  { code: "PA", name: "Panama" },
  { code: "PG", name: "Papua New Guinea" },
  { code: "PY", name: "Paraguay" },
  { code: "PE", name: "Peru" },
  { code: "PH", name: "Philippines" },
  { code: "PL", name: "Poland" },
  { code: "PT", name: "Portugal" },
  { code: "QA", name: "Qatar" },
  { code: "RO", name: "Romania" },
  { code: "RU", name: "Russia" },
  { code: "RW", name: "Rwanda" },
  { code: "KN", name: "Saint Kitts and Nevis" },
  { code: "LC", name: "Saint Lucia" },
  { code: "VC", name: "Saint Vincent and the Grenadines" },
  { code: "WS", name: "Samoa" },
  { code: "SM", name: "San Marino" },
  { code: "ST", name: "Sao Tome and Principe" },
  { code: "SA", name: "Saudi Arabia" },
  { code: "SN", name: "Senegal" },
  { code: "RS", name: "Serbia" },
  { code: "SC", name: "Seychelles" },
  { code: "SL", name: "Sierra Leone" },
  { code: "SG", name: "Singapore" },
  { code: "SK", name: "Slovakia" },
  { code: "SI", name: "Slovenia" },
  { code: "SB", name: "Solomon Islands" },
  { code: "SO", name: "Somalia" },
  { code: "ZA", name: "South Africa" },
  { code: "KR", name: "South Korea" },
  { code: "SS", name: "South Sudan" },
  { code: "ES", name: "Spain" },
  { code: "LK", name: "Sri Lanka" },
  { code: "SD", name: "Sudan" },
  { code: "SR", name: "Suriname" },
  { code: "SE", name: "Sweden" },
  { code: "CH", name: "Switzerland" },
  { code: "SY", name: "Syria" },
  { code: "TW", name: "Taiwan" },
  { code: "TJ", name: "Tajikistan" },
  { code: "TZ", name: "Tanzania" },
  { code: "TH", name: "Thailand" },
  { code: "TL", name: "Timor-Leste" },
  { code: "TG", name: "Togo" },
  { code: "TO", name: "Tonga" },
  { code: "TT", name: "Trinidad and Tobago" },
  { code: "TN", name: "Tunisia" },
  { code: "TR", name: "Turkey" },
  { code: "TM", name: "Turkmenistan" },
  { code: "TV", name: "Tuvalu" },
  { code: "UG", name: "Uganda" },
  { code: "UA", name: "Ukraine" },
  { code: "AE", name: "United Arab Emirates" },
  { code: "GB", name: "United Kingdom" },
  { code: "US", name: "United States" },
  { code: "UY", name: "Uruguay" },
  { code: "UZ", name: "Uzbekistan" },
  { code: "VU", name: "Vanuatu" },
  { code: "VA", name: "Vatican City" },
  { code: "VE", name: "Venezuela" },
  { code: "VN", name: "Vietnam" },
  { code: "YE", name: "Yemen" },
  { code: "ZM", name: "Zambia" },
  { code: "ZW", name: "Zimbabwe" },
] as const;

// Tax ID configuration per country
interface TaxIdConfig {
  type: string;
  label: string;
  placeholder: string;
  pattern: RegExp;
  hint: string;
}

const TAX_ID_CONFIG: Record<string, TaxIdConfig> = {
  IN: {
    type: "gst_in",
    label: "GST Number (GSTIN)",
    placeholder: "22AAAAA0000A1Z5",
    pattern: /^\d{2}[A-Z]{5}\d{4}[A-Z]{1}[A-Z\d]{1}[Z]{1}[A-Z\d]{1}$/,
    hint: "15-character alphanumeric GST Identification Number",
  },
  US: {
    type: "us_ein",
    label: "EIN (Employer Identification Number)",
    placeholder: "12-3456789",
    pattern: /^\d{2}-?\d{7}$/,
    hint: "Federal Employer Identification Number (XX-XXXXXXX)",
  },
  GB: {
    type: "gb_vat",
    label: "VAT Number",
    placeholder: "GB123456789",
    pattern: /^GB\d{9}(\d{3})?$/,
    hint: "UK VAT registration number (GB followed by 9 or 12 digits)",
  },
  DE: {
    type: "eu_vat",
    label: "VAT Number (USt-IdNr.)",
    placeholder: "DE123456789",
    pattern: /^DE\d{9}$/,
    hint: "German VAT identification number",
  },
  FR: {
    type: "eu_vat",
    label: "VAT Number (TVA)",
    placeholder: "FR12345678901",
    pattern: /^FR[A-Z0-9]{2}\d{9}$/,
    hint: "French VAT identification number",
  },
  AU: {
    type: "au_abn",
    label: "ABN (Australian Business Number)",
    placeholder: "51 824 753 556",
    pattern: /^\d{2}\s?\d{3}\s?\d{3}\s?\d{3}$/,
    hint: "11-digit Australian Business Number",
  },
  CA: {
    type: "ca_bn",
    label: "Business Number (BN)",
    placeholder: "123456789RT0001",
    pattern: /^\d{9}[A-Z]{2}\d{4}$/,
    hint: "15-character CRA Business Number",
  },
  SG: {
    type: "sg_gst",
    label: "GST Registration Number",
    placeholder: "M12345678X",
    pattern: /^[A-Z]\d{8}[A-Z]$/,
    hint: "Singapore GST registration number",
  },
  JP: {
    type: "jp_cn",
    label: "Corporate Number (法人番号)",
    placeholder: "1234567890123",
    pattern: /^\d{13}$/,
    hint: "13-digit Japanese Corporate Number",
  },
  BR: {
    type: "br_cnpj",
    label: "CNPJ",
    placeholder: "12.345.678/0001-00",
    pattern: /^\d{2}\.?\d{3}\.?\d{3}\/?\d{4}-?\d{2}$/,
    hint: "Brazilian business registration number",
  },
};

// EU member states that use eu_vat type (beyond DE and FR which have specific entries)
const EU_VAT_COUNTRIES = [
  "AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "GR",
  "HU", "IE", "IT", "LV", "LT", "LU", "MT", "NL", "PL", "PT",
  "RO", "SK", "SI", "ES", "SE",
];

function getTaxIdConfig(countryCode: string): TaxIdConfig | null {
  if (TAX_ID_CONFIG[countryCode]) return TAX_ID_CONFIG[countryCode];
  if (EU_VAT_COUNTRIES.includes(countryCode)) {
    return {
      type: "eu_vat",
      label: "VAT Number",
      placeholder: `${countryCode}123456789`,
      pattern: /^[A-Z]{2}[A-Z0-9]{2,13}$/,
      hint: `EU VAT identification number (starts with ${countryCode})`,
    };
  }
  return null;
}

// Country-specific address labels
function getAddressLabels(countryCode: string) {
  switch (countryCode) {
    case "US":
      return { state: "State", postalCode: "ZIP Code" };
    case "GB":
      return { state: "County", postalCode: "Postcode" };
    case "CA":
      return { state: "Province", postalCode: "Postal Code" };
    case "AU":
      return { state: "State/Territory", postalCode: "Postcode" };
    case "IN":
      return { state: "State", postalCode: "PIN Code" };
    case "JP":
      return { state: "Prefecture", postalCode: "Postal Code" };
    case "BR":
      return { state: "State", postalCode: "CEP" };
    default:
      return { state: "State / Province / Region", postalCode: "Postal Code" };
  }
}

// Indian states for GST
const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
  "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand",
  "Karnataka", "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur",
  "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab",
  "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura",
  "Uttar Pradesh", "Uttarakhand", "West Bengal",
  "Andaman and Nicobar Islands", "Chandigarh", "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi", "Jammu and Kashmir", "Ladakh", "Lakshadweep", "Puducherry",
];

// Currency mapping for common countries
const COUNTRY_CURRENCY: Record<string, string> = {
  US: "USD", GB: "GBP", IN: "INR", JP: "JPY", AU: "AUD", CA: "CAD",
  CH: "CHF", CN: "CNY", BR: "BRL", SG: "SGD", HK: "HKD", KR: "KRW",
  MX: "MXN", ZA: "ZAR", AE: "AED", SA: "SAR", NZ: "NZD", SE: "SEK",
  NO: "NOK", DK: "DKK", PL: "PLN", CZ: "CZK", HU: "HUF", RO: "RON",
  BG: "BGN", TH: "THB", MY: "MYR", PH: "PHP", ID: "IDR", VN: "VND",
  TR: "TRY", RU: "RUB", UA: "UAH", EG: "EGP", NG: "NGN", KE: "KES",
  PK: "PKR", BD: "BDT", LK: "LKR", NP: "NPR",
};

// Most EU countries use EUR
const EUR_COUNTRIES = [
  "AT", "BE", "CY", "EE", "FI", "FR", "DE", "GR", "IE", "IT",
  "LV", "LT", "LU", "MT", "NL", "PT", "SK", "SI", "ES",
];

function getDefaultCurrency(countryCode: string): string {
  if (EUR_COUNTRIES.includes(countryCode)) return "EUR";
  return COUNTRY_CURRENCY[countryCode] || "USD";
}

const CURRENCIES = [
  { code: "USD", name: "US Dollar" },
  { code: "EUR", name: "Euro" },
  { code: "GBP", name: "British Pound" },
  { code: "INR", name: "Indian Rupee" },
  { code: "JPY", name: "Japanese Yen" },
  { code: "AUD", name: "Australian Dollar" },
  { code: "CAD", name: "Canadian Dollar" },
  { code: "CHF", name: "Swiss Franc" },
  { code: "CNY", name: "Chinese Yuan" },
  { code: "SGD", name: "Singapore Dollar" },
  { code: "HKD", name: "Hong Kong Dollar" },
  { code: "KRW", name: "South Korean Won" },
  { code: "BRL", name: "Brazilian Real" },
  { code: "MXN", name: "Mexican Peso" },
  { code: "ZAR", name: "South African Rand" },
  { code: "AED", name: "UAE Dirham" },
  { code: "SAR", name: "Saudi Riyal" },
  { code: "NZD", name: "New Zealand Dollar" },
  { code: "SEK", name: "Swedish Krona" },
  { code: "NOK", name: "Norwegian Krone" },
  { code: "DKK", name: "Danish Krone" },
  { code: "PLN", name: "Polish Zloty" },
  { code: "THB", name: "Thai Baht" },
  { code: "MYR", name: "Malaysian Ringgit" },
  { code: "IDR", name: "Indonesian Rupiah" },
  { code: "TRY", name: "Turkish Lira" },
  { code: "PKR", name: "Pakistani Rupee" },
  { code: "BDT", name: "Bangladeshi Taka" },
  { code: "NGN", name: "Nigerian Naira" },
  { code: "EGP", name: "Egyptian Pound" },
  { code: "KES", name: "Kenyan Shilling" },
];

interface BillingFormState {
  country: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  postalCode: string;
  taxId: string;
  taxIdType: string;
  currency: string;
  email: string;
  phone: string;
}

const BillingInfo = () => {
  const { clientInfo } = useAuth();
  const { toast } = useToast();

  const [form, setForm] = useState<BillingFormState>({
    country: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    postalCode: "",
    taxId: "",
    taxIdType: "",
    currency: "USD",
    email: "",
    phone: "",
  });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [taxIdError, setTaxIdError] = useState<string | null>(null);

  useEffect(() => {
    if (!clientInfo) return;
    const fetchBilling = async () => {
      const { data } = await supabase
        .from("client")
        .select(
          "billing_country, billing_address_line1, billing_address_line2, billing_city, billing_state, billing_postal_code, billing_tax_id, billing_tax_id_type, billing_currency, billing_email, billing_phone"
        )
        .eq("id", clientInfo.id)
        .single();
      if (data) {
        setForm({
          country: data.billing_country || "",
          addressLine1: data.billing_address_line1 || "",
          addressLine2: data.billing_address_line2 || "",
          city: data.billing_city || "",
          state: data.billing_state || "",
          postalCode: data.billing_postal_code || "",
          taxId: data.billing_tax_id || "",
          taxIdType: data.billing_tax_id_type || "",
          currency: data.billing_currency || "USD",
          email: data.billing_email || "",
          phone: data.billing_phone || "",
        });
      }
      setLoading(false);
    };
    fetchBilling();
  }, [clientInfo]);

  const updateField = (field: keyof BillingFormState, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (field === "taxId") setTaxIdError(null);
  };

  const handleCountryChange = (countryCode: string) => {
    const taxConfig = getTaxIdConfig(countryCode);
    const newCurrency = getDefaultCurrency(countryCode);
    setForm((prev) => ({
      ...prev,
      country: countryCode,
      taxId: "",
      taxIdType: taxConfig?.type || "",
      currency: newCurrency,
      state: "",
    }));
    setTaxIdError(null);
  };

  const validateTaxId = (): boolean => {
    if (!form.taxId) return true; // Tax ID is optional
    const config = getTaxIdConfig(form.country);
    if (!config) return true;
    if (!config.pattern.test(form.taxId.toUpperCase())) {
      setTaxIdError(`Invalid format. ${config.hint}`);
      return false;
    }
    setTaxIdError(null);
    return true;
  };

  const handleSave = async () => {
    if (!clientInfo) return;
    if (!validateTaxId()) return;

    setSaving(true);
    const { error } = await supabase
      .from("client")
      .update({
        billing_country: form.country || null,
        billing_address_line1: form.addressLine1.trim() || null,
        billing_address_line2: form.addressLine2.trim() || null,
        billing_city: form.city.trim() || null,
        billing_state: form.state.trim() || null,
        billing_postal_code: form.postalCode.trim() || null,
        billing_tax_id: form.taxId.trim().toUpperCase() || null,
        billing_tax_id_type: form.taxIdType || null,
        billing_currency: form.currency || null,
        billing_email: form.email.trim() || null,
        billing_phone: form.phone.trim() || null,
      })
      .eq("id", clientInfo.id);

    setSaving(false);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Saved", description: "Billing information updated successfully." });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const taxConfig = getTaxIdConfig(form.country);
  const addressLabels = getAddressLabels(form.country);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Receipt className="h-5 w-5" /> Billing Information
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Configure your billing address and tax details for invoicing.
        </p>
      </div>

      {/* Country Selection */}
      <div className="max-w-md space-y-2">
        <Label htmlFor="billing-country">Country</Label>
        <Select value={form.country} onValueChange={handleCountryChange}>
          <SelectTrigger id="billing-country">
            <SelectValue placeholder="Select country" />
          </SelectTrigger>
          <SelectContent>
            {COUNTRIES.map((c) => (
              <SelectItem key={c.code} value={c.code}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {form.country && (
        <>
          {/* Billing Address */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
              Billing Address
            </h3>
            <div className="max-w-md space-y-3">
              <div className="space-y-2">
                <Label htmlFor="billing-line1">Address Line 1</Label>
                <Input
                  id="billing-line1"
                  value={form.addressLine1}
                  onChange={(e) => updateField("addressLine1", e.target.value)}
                  placeholder="Street address, P.O. box"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="billing-line2">Address Line 2</Label>
                <Input
                  id="billing-line2"
                  value={form.addressLine2}
                  onChange={(e) => updateField("addressLine2", e.target.value)}
                  placeholder="Apartment, suite, unit, floor"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="billing-city">City</Label>
                  <Input
                    id="billing-city"
                    value={form.city}
                    onChange={(e) => updateField("city", e.target.value)}
                    placeholder="City"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="billing-state">{addressLabels.state}</Label>
                  {form.country === "IN" ? (
                    <Select value={form.state} onValueChange={(v) => updateField("state", v)}>
                      <SelectTrigger id="billing-state">
                        <SelectValue placeholder="Select state" />
                      </SelectTrigger>
                      <SelectContent>
                        {INDIAN_STATES.map((s) => (
                          <SelectItem key={s} value={s}>
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      id="billing-state"
                      value={form.state}
                      onChange={(e) => updateField("state", e.target.value)}
                      placeholder={addressLabels.state}
                    />
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="billing-postal">{addressLabels.postalCode}</Label>
                  <Input
                    id="billing-postal"
                    value={form.postalCode}
                    onChange={(e) => updateField("postalCode", e.target.value)}
                    placeholder={addressLabels.postalCode}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Country</Label>
                  <Input
                    value={COUNTRIES.find((c) => c.code === form.country)?.name || ""}
                    disabled
                    className="bg-muted"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Tax Information - shown when country has tax ID config */}
          {taxConfig && (
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                Tax Information
              </h3>
              <div className="max-w-md space-y-2">
                <Label htmlFor="billing-tax-id">{taxConfig.label}</Label>
                <Input
                  id="billing-tax-id"
                  value={form.taxId}
                  onChange={(e) => updateField("taxId", e.target.value.toUpperCase())}
                  placeholder={taxConfig.placeholder}
                  className={taxIdError ? "border-destructive" : ""}
                />
                {taxIdError ? (
                  <p className="text-xs text-destructive">{taxIdError}</p>
                ) : (
                  <p className="text-xs text-muted-foreground">{taxConfig.hint}</p>
                )}
              </div>
            </div>
          )}

          {/* Billing Contact */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
              Billing Contact
            </h3>
            <div className="max-w-md space-y-3">
              <div className="space-y-2">
                <Label htmlFor="billing-email">Billing Email</Label>
                <Input
                  id="billing-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => updateField("email", e.target.value)}
                  placeholder="billing@company.com"
                />
                <p className="text-xs text-muted-foreground">
                  Invoices and billing notifications will be sent to this address.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="billing-phone">Phone Number</Label>
                <Input
                  id="billing-phone"
                  type="tel"
                  value={form.phone}
                  onChange={(e) => updateField("phone", e.target.value)}
                  placeholder={form.country === "IN" ? "+91 98765 43210" : "+1 (555) 123-4567"}
                />
              </div>
            </div>
          </div>

          {/* Currency Preference */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
              Currency
            </h3>
            <div className="max-w-md space-y-2">
              <Label htmlFor="billing-currency">Preferred Billing Currency</Label>
              <Select value={form.currency} onValueChange={(v) => updateField("currency", v)}>
                <SelectTrigger id="billing-currency">
                  <SelectValue placeholder="Select currency" />
                </SelectTrigger>
                <SelectContent>
                  {CURRENCIES.map((c) => (
                    <SelectItem key={c.code} value={c.code}>
                      {c.code} — {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Save Button */}
          <div className="max-w-md pt-2">
            <Button onClick={handleSave} disabled={saving} className="w-full sm:w-auto">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save Billing Information
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default BillingInfo;
